/**
 * global init 请求体与响应映射（原 layout 中 getGlobalInitData 一段）
 * 供服务端 layout 与客户端 usePublicResourceRefresh 复用，避免 payload 分叉。
 */
import type { GlobalInitRequest } from '@/api/types/api-definition';
import type { DingTongConfig } from '@/api/types/ding-tong';
import type {
  Country,
  CountrySite,
  Currency,
  IMemberLevelData,
  PublicResourceData,
} from '@/api/types/public';
import { NAV_TYPE } from '@/utils/constants';

export type GlobalInitChromeMapped = {
  currencyListNormalized: Currency[];
  publicResourceData: PublicResourceData;
  countryList: Country[];
  countrySiteList: CountrySite[];
  memberLevelData: IMemberLevelData | undefined;
  footerNavList: unknown[];
  headerNavList: unknown[];
  logoData: unknown | null;
  dtInfo: DingTongConfig | null;
  quickEntryData: unknown | null;
  noticeData: unknown | null;
  /** global/init 返回的 compliance_policies，同步写入 store.compliancePolicies */
  compliancePolicies: Record<string, unknown> | null;
};

export type GlobalInitNavMode = 'published' | 'pc-header-draft';

type GlobalInitRequestOptions = {
  navMode?: GlobalInitNavMode;
};

export function buildGlobalInitRequestPayload(
  isHomePreview: boolean,
  { navMode = 'published' }: GlobalInitRequestOptions = {},
): GlobalInitRequest {
  const isPcHeaderDraft = navMode === 'pc-header-draft';

  return {
    info: [
      { key: 'product_config' },
      { key: 'show_my_card' },
      { key: 'cart_default_check_all' },
      { key: 'alive_lottery' },
      { key: 'register_gift' },
      { key: 'timestamp' },
    ],
    blocks: [
      { block_type: 13, status: isHomePreview ? 2 : 1 },
      { block_type: 9, status: isHomePreview ? 2 : 1 },
    ],
    config_names: [
      'product_filter_new',
      'product_sort_new',
      'product_content_free_shipping_member',
      'product_content_half_price_member',
    ],
    country: true,
    country_site: true,
    crm_point: false,
    cart_rec_source: true,
    currency: true,
    ding_tong: true,
    ding_tong_pages: [1, 2, 3, 4],
    half_price: false,
    ip_country: true,
    jsonvalues: [
      'tracking_config',
      'user_info_collection_info',
      'instadesk_chat',
      'prescription_recognition_open',
      'customer_service_popup',
      'change_email_switch',
      'contact_chat_open_time',
      'cart_empty_navigation_config',
      'cart_FSA',
      'compliance_subscription_display',
      'klarna_promotion',
      'address_auto_complete_area',
    ],
    member_levels: true,
    nav: isPcHeaderDraft
      ? {
          is_draft: true,
          types: [NAV_TYPE.WEB_HEADER, NAV_TYPE.WEB_FOOTER, NAV_TYPE.WAP_FOOTER],
          draft_type: NAV_TYPE.WEB_HEADER,
        }
      : {
          types: [
            NAV_TYPE.WEB_HEADER,
            NAV_TYPE.WEB_FOOTER,
            NAV_TYPE.WAP_HEADER,
            NAV_TYPE.WAP_FOOTER,
          ],
        },
    wish_count: false,
    logo: true,
    notice: true,
    quick_entry: true,
    compliance_policies: 1,
  } as GlobalInitRequest;
}

export function unwrapGlobalInitResponse(res: unknown): Record<string, unknown> {
  if (res && typeof res === 'object' && 'data' in res && (res as { data?: unknown }).data != null) {
    const d = (res as { data: Record<string, unknown> }).data;
    return typeof d === 'object' && d !== null ? d : {};
  }
  return (res as Record<string, unknown>) || {};
}

export function normalizeCurrencyList(raw: Currency[] | undefined): Currency[] {
  if (!raw?.length) return [];
  return raw.map((item: Currency & { symbol_suffix?: string }) => ({
    ...item,
    name: item.name || item.symbol_suffix?.trim() || 'USD',
  }));
}

export function mapGlobalInitResponseToChromeState(
  globalInitData: Record<string, unknown>,
  isWeb: boolean,
): GlobalInitChromeMapped {
  const currencyListNormalized = normalizeCurrencyList(globalInitData?.currency as Currency[]);

  const publicResourceData = {
    cart_rec_source: globalInitData?.cart_rec_source as 1 | 2 | undefined,
    info: globalInitData?.info || {},
    config_names: globalInitData?.config_names || {},
    jsonvalues: globalInitData?.jsonvalues || {},
    blocks: globalInitData?.blocks || globalInitData?.block_types || {},
  } as PublicResourceData;

  const compliancePoliciesRaw = globalInitData?.compliance_policies;
  const compliancePolicies =
    compliancePoliciesRaw != null && typeof compliancePoliciesRaw === 'object'
      ? (compliancePoliciesRaw as Record<string, unknown>)
      : null;

  const countryList = (globalInitData?.country as Country[]) || [];
  const countrySiteList = (globalInitData?.country_site as CountrySite[]) || [];
  const memberLevelData = globalInitData?.member_levels as IMemberLevelData | undefined;

  const navData = Array.isArray(globalInitData?.nav) ? (globalInitData.nav as unknown[]) : [];
  const targetFooterType = isWeb ? NAV_TYPE.WEB_FOOTER : NAV_TYPE.WAP_FOOTER;
  const targetHeaderType = isWeb ? NAV_TYPE.WEB_HEADER : NAV_TYPE.WAP_HEADER;

  const footerNavList = navData.filter((n: { type?: number }) => n.type === targetFooterType);
  const headerNavList = navData.filter((n: { type?: number }) => n.type === targetHeaderType);

  const getListItems = (data: unknown, componentId: string) => {
    if (!data) return [];
    const items = Array.isArray(data) ? data : [data];
    return items.map((item: Record<string, unknown>) => ({
      ...item,
      component_id: item.component_id || componentId,
    }));
  };

  const homePageData = {
    list: [
      ...getListItems(globalInitData?.logo, 'logo'),
      ...getListItems(globalInitData?.ding_tong, 'dingTong'),
      ...getListItems(globalInitData?.quick_entry, 'quickEntry'),
      ...getListItems(globalInitData?.notice, 'notice'),
    ].filter(Boolean),
  };

  headerNavList.forEach(
    (item: { children?: { template_id?: number; imageTemplateIndex?: number }[] }) => {
      if (item.children?.length) {
        let imageIndex = 0;
        item.children.forEach((child) => {
          if (child.template_id === 3) {
            child.imageTemplateIndex = imageIndex;
            imageIndex++;
          }
        });
      }
    },
  );

  const list = homePageData.list as { component_id?: string }[];
  const logoData = list.find((item) => item?.component_id === 'logo') || null;
  const dtInfo =
    (list.find((item) => item?.component_id === 'dingTong') as DingTongConfig | undefined) || null;
  const quickEntryData = list.find((item) => item?.component_id === 'quickEntry') || null;
  const noticeData = list.find((item) => item?.component_id === 'notice') || null;

  return {
    currencyListNormalized,
    publicResourceData,
    countryList,
    countrySiteList,
    memberLevelData,
    footerNavList,
    headerNavList,
    logoData,
    dtInfo,
    quickEntryData,
    noticeData,
    compliancePolicies,
  };
}
